import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const MfaSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [setupData, setSetupData] = useState(null); // {qr_data_url, manual_secret}
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/mfa/status`, authHeaders());
        setEnabled(!!r.data.mfa_enabled);
        setRemaining(r.data.recovery_codes_remaining ?? null);
      } catch (e) { /* silencioso */ }
      finally { setLoading(false); }
    })();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/setup`, {}, authHeaders());
      setSetupData(r.data);
      setRecoveryCodes(null);
      setCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la configuración');
    } finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/enable`, { code: code.trim() }, authHeaders());
      setRecoveryCodes(r.data.recovery_codes || []);
      setRemaining((r.data.recovery_codes || []).length);
      setSetupData(null);
      setEnabled(true);
      toast.success('MFA activado correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código inválido');
    } finally { setBusy(false); }
  };

  const disableMfa = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { password: disablePassword }, authHeaders());
      setEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      setRecoveryCodes(null);
      setRemaining(0);
      toast.success('MFA desactivado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo desactivar');
    } finally { setBusy(false); }
  };

  const regenerateRecovery = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/regenerate-recovery`, { password: regenPassword }, authHeaders());
      setRecoveryCodes(r.data.recovery_codes || []);
      setRemaining((r.data.recovery_codes || []).length);
      setShowRegen(false);
      setRegenPassword('');
      toast.success('Se generaron nuevos códigos de respaldo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron regenerar');
    } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    try {
      navigator.clipboard.writeText((recoveryCodes || []).join('\n'));
      toast.success('Códigos copiados');
    } catch (e) { /* noop */ }
  };

  return (
    <Card className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm" data-testid="mfa-settings-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Verificación en dos pasos (MFA)</h2>
          <p className="text-sm text-slate-600">Protege tu cuenta con un código de tu app de autenticación (Google Authenticator).</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Cargando...</p>
      ) : (
        <>
          {/* Estado actual */}
          <div className="flex items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-testid="mfa-status-badge">
              {enabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
              {enabled ? 'MFA activo' : 'MFA desactivado'}
            </span>
          </div>

          {/* Estado de códigos de respaldo + aviso cuando quedan pocos */}
          {enabled && remaining !== null && !recoveryCodes && (
            <div className="mb-4">
              {remaining <= 2 ? (
                <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-2" data-testid="mfa-recovery-warning">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold">Te queda{remaining === 1 ? '' : 'n'} solo {remaining} código{remaining === 1 ? '' : 's'} de respaldo.</p>
                    <p>Regenera tus códigos para no quedarte sin forma de entrar si pierdes tu teléfono.</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600" data-testid="mfa-recovery-count">Te quedan <strong>{remaining}</strong> códigos de respaldo.</p>
              )}
              {!showRegen ? (
                <Button type="button" variant="outline" onClick={() => setShowRegen(true)} className="mt-3 h-9" data-testid="mfa-regen-start">
                  <RefreshCw className="w-4 h-4 mr-2" /> Regenerar códigos de respaldo
                </Button>
              ) : (
                <div className="mt-3 space-y-2 max-w-xs" data-testid="mfa-regen-flow">
                  <Label htmlFor="mfa-regen-pass">Confirma tu contraseña para generar códigos nuevos</Label>
                  <Input id="mfa-regen-pass" type="password" data-testid="mfa-regen-password-input" value={regenPassword} onChange={(e) => setRegenPassword(e.target.value)} placeholder="Tu contraseña" />
                  <p className="text-xs text-slate-500">Al regenerar, los códigos de respaldo anteriores dejarán de servir.</p>
                  <div className="flex gap-2">
                    <Button type="button" onClick={regenerateRecovery} disabled={busy || !regenPassword} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-regen-confirm">
                      {busy ? 'Generando...' : 'Generar nuevos códigos'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowRegen(false); setRegenPassword(''); }}>Cancelar</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Códigos de respaldo recién generados */}
          {recoveryCodes && (
            <div className="mb-4 p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="mfa-recovery-codes">
              <p className="text-sm font-semibold text-amber-800 mb-2">Guarda estos códigos de respaldo en un lugar seguro. Cada uno sirve una sola vez y te permiten entrar si pierdes tu teléfono.</p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-800 mb-3">
                {recoveryCodes.map((c) => <div key={c} className="bg-white rounded px-2 py-1 border border-amber-200 text-center">{c}</div>)}
              </div>
              <Button type="button" onClick={copyRecovery} variant="outline" className="h-9" data-testid="mfa-copy-recovery">
                <Copy className="w-4 h-4 mr-2" /> Copiar códigos
              </Button>
            </div>
          )}

          {/* MFA desactivado y sin flujo en curso: botón activar */}
          {!enabled && !setupData && (
            <Button type="button" onClick={startSetup} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-enable-start">
              <ShieldCheck className="w-4 h-4 mr-2" /> {busy ? 'Generando...' : 'Activar MFA'}
            </Button>
          )}

          {/* Flujo de configuración: QR + código */}
          {setupData && (
            <div className="space-y-4" data-testid="mfa-setup-flow">
              <p className="text-sm text-slate-700">1. Abre <strong>Google Authenticator</strong> y escanea este código QR:</p>
              <img src={setupData.qr_data_url} alt="Código QR MFA" className="w-44 h-44 border border-slate-200 rounded-lg" data-testid="mfa-qr" />
              <p className="text-xs text-slate-500">¿No puedes escanear? Ingresa esta clave manualmente:<br /><span className="font-mono text-slate-700 break-all">{setupData.manual_secret}</span></p>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="mfa-enable-code">2. Escribe el código de 6 dígitos que aparece en la app:</Label>
                <Input id="mfa-enable-code" data-testid="mfa-enable-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="tracking-widest text-center" />
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={confirmEnable} disabled={busy || code.trim().length < 6} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-enable-confirm">
                  {busy ? 'Verificando...' : 'Confirmar y activar'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSetupData(null); setCode(''); }} data-testid="mfa-setup-cancel">Cancelar</Button>
              </div>
            </div>
          )}

          {/* MFA activo: opción de desactivar */}
          {enabled && !showDisable && (
            <Button type="button" variant="outline" onClick={() => setShowDisable(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="mfa-disable-start">
              <ShieldOff className="w-4 h-4 mr-2" /> Desactivar MFA
            </Button>
          )}
          {enabled && showDisable && (
            <div className="space-y-3 max-w-xs" data-testid="mfa-disable-flow">
              <Label htmlFor="mfa-disable-pass">Confirma tu contraseña para desactivar MFA</Label>
              <Input id="mfa-disable-pass" type="password" data-testid="mfa-disable-password-input" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} placeholder="Tu contraseña" />
              <div className="flex gap-2">
                <Button type="button" onClick={disableMfa} disabled={busy || !disablePassword} className="bg-red-600 hover:bg-red-700" data-testid="mfa-disable-confirm">
                  {busy ? 'Desactivando...' : 'Desactivar'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>Cancelar</Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default MfaSettingsCard;
